import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Nav sale-card hydration (BRD perf).
 *
 * The header (`header-alternate`) renders the menu structure with empty
 * `.nav-drawer__sale-cards[data-ops]` containers, where `data-ops` is a
 * comma-separated list of operation handles. On idle, this module fetches the
 * fetch-only `nav-sale-cards` section once via the Section Rendering API,
 * builds a handle -> card map, and clones the matching cards into each
 * container for the current viewport. The cards are byte-identical to the
 * server-rendered ones (same `operation-card` snippet), and the menu grouping
 * stays in Liquid, so there is no visual or behavioural change.
 */

const DESKTOP_BREAKPOINT = 990;
const SECTION_ID = 'nav-sale-cards';

/** @type {Promise<{ map: Map<string, Element>, scripts: string[] }> | null} */
let cardMapPromise = null;
let scriptsLoaded = false;

const isDesktop = () => window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;

/**
 * Fetches the source section once and resolves to a handle -> card element map,
 * plus the unique script srcs the cards depend on (e.g. the coming-soon
 * countdown and reminder web components).
 * @returns {Promise<{ map: Map<string, Element>, scripts: string[] }>}
 */
function getCardMap() {
  cardMapPromise ??= sectionRenderer
    .getSectionHTML(SECTION_ID)
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const map = new Map();

      for (const item of doc.querySelectorAll('.nav-sale-cards-source__item[data-op-handle]')) {
        const card = item.querySelector('.operation-card');
        if (card) map.set(item.dataset.opHandle, card);
      }

      const scripts = [...new Set([...doc.querySelectorAll('script[src]')].map((s) => s.src))];

      return { map, scripts };
    })
    .catch((error) => {
      cardMapPromise = null; // allow a retry on the next trigger
      throw error;
    });

  return cardMapPromise;
}

/**
 * Loads the card dependency scripts once. Cloned <script> nodes do not execute,
 * so the web components inside coming-soon cards (operation-countdown,
 * operation-reminder) are loaded here instead. Module scripts dedupe by URL.
 * @param {string[]} scripts
 */
function loadScripts(scripts) {
  if (scriptsLoaded) return;
  scriptsLoaded = true;
  for (const src of scripts) {
    if (document.querySelector(`script[src="${src}"]`)) continue;
    const el = document.createElement('script');
    el.type = 'module';
    el.src = src;
    document.body.appendChild(el);
  }
}

/**
 * Card containers for the given viewport that still need hydrating.
 * @param {boolean} desktop
 * @returns {Element[]}
 */
function pendingContainers(desktop) {
  return [...document.querySelectorAll('.nav-drawer__sale-cards[data-ops]')].filter((container) => {
    if (container.dataset.navHydrated === 'true') return false;
    return Boolean(container.closest('.nav-drawer__desktop-panels')) === desktop;
  });
}

/**
 * Hydrates the current viewport's empty card containers from the source section.
 * @param {boolean} [desktop]
 */
async function hydrateNavCards(desktop = isDesktop()) {
  const containers = pendingContainers(desktop);
  if (containers.length === 0) return;

  let data;
  try {
    data = await getCardMap();
  } catch {
    return; // network error: menu links still work, containers stay empty
  }
  const { map, scripts } = data;
  if (!map?.size) return;

  for (const container of containers) {
    if (container.dataset.navHydrated === 'true') continue;

    const handles = (container.dataset.ops ?? '').split(',').map((h) => h.trim()).filter(Boolean);
    const fragment = document.createDocumentFragment();

    for (const handle of handles) {
      const card = map.get(handle);
      if (!card) continue;
      const clone = card.cloneNode(true);
      // Inert cloned <script> nodes never execute; loadScripts handles deps.
      clone.querySelectorAll('script').forEach((node) => node.remove());
      fragment.appendChild(clone);
    }

    if (fragment.childNodes.length > 0) {
      container.appendChild(fragment);
      container.dataset.navHydrated = 'true';
    }
  }

  loadScripts(scripts);
}

onDocumentReady(() => {
  requestIdleCallback(() => hydrateNavCards());

  // Hydrate the other viewport's cards when the breakpoint is crossed.
  window
    .matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`)
    .addEventListener('change', () => hydrateNavCards());

  // Safety net: hydrate immediately if the menu is opened before idle fired.
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.target.closest?.('.menu-drawer, nav-drawer, .header-actions__action, [data-nav-trigger]')) {
        hydrateNavCards();
      }
    },
    { capture: true }
  );
});
